import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

export interface SearchSelectOption {
  id: string;
  label: string;
  description?: string;
}

interface SearchSelectProps {
  options: SearchSelectOption[];
  suggestedOptions?: SearchSelectOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  noResultsText?: string;
  suggestedLabel?: string;
  /** Heading of the full list when suggestions are shown above it. */
  optionsLabel?: string;
  /**
   * Keep suggested entries in the full list as well, so it always shows every option — the
   * suggestions are a shortcut, not a split. Off by default (import steps de-duplicate).
   */
  showAllOptions?: boolean;
  /**
   * Render the list in the flow instead of floating over the content: inside a dialog it then
   * grows the dialog (up to its max height) instead of opening a second scroll area in it.
   */
  inlineDropdown?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

const OVERLAY_SHADOW = 'shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]';

export default function SearchSelect({
  options,
  suggestedOptions,
  value,
  onChange,
  placeholder = 'Suchen…',
  noResultsText = 'Keine Treffer',
  suggestedLabel = 'Vorschläge',
  optionsLabel,
  showAllOptions = false,
  inlineDropdown = false,
  label,
  required,
  disabled = false,
  className = '',
}: SearchSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === value) ?? suggestedOptions?.find((o) => o.id === value),
    [options, suggestedOptions, value]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q)
    );
  }, [options, search]);

  const filteredSuggestions = useMemo(() => {
    if (!suggestedOptions?.length) return [];
    if (!search.trim()) return suggestedOptions;
    const q = search.toLowerCase();
    return suggestedOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q)
    );
  }, [suggestedOptions, search]);

  const suggestedIds = useMemo(
    () => new Set(filteredSuggestions.map((o) => o.id)),
    [filteredSuggestions]
  );
  const mainOptions = useMemo(
    () => (showAllOptions ? filteredOptions : filteredOptions.filter((o) => !suggestedIds.has(o.id))),
    [filteredOptions, suggestedIds, showAllOptions]
  );

  const hasResults = filteredSuggestions.length > 0 || mainOptions.length > 0;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // An inline list grows its container; when that container is already at its max height
  // (e.g. a dialog on a small screen), bring the list into view instead of leaving it cut off.
  useEffect(() => {
    if (isOpen && inlineDropdown) dropdownRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, inlineDropdown]);

  function handleSelect(id: string) {
    onChange(id);
    setSearch('');
    setIsOpen(false);
  }

  function handleClear() {
    onChange('');
    setSearch('');
    setIsOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-[12px] font-medium text-slate-600 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {disabled ? (
        <div className="flex items-center w-full px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200 text-[13px] text-slate-500 cursor-not-allowed">
          <div className="flex-1 min-w-0">
            <div className="truncate">{selectedOption?.label ?? placeholder}</div>
            {selectedOption?.description && (
              <div className="text-[11px] text-slate-400 truncate">
                {selectedOption.description}
              </div>
            )}
          </div>
        </div>
      ) : value && selectedOption ? (
        <div className="flex items-center gap-2 w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900">
          <div className="flex-1 min-w-0">
            <div className="truncate">{selectedOption.label}</div>
            {selectedOption.description && (
              <div className="text-[11px] text-slate-500 truncate">
                {selectedOption.description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            title="Auswahl entfernen"
            className="flex-shrink-0 p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-50"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" strokeWidth={2} />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full pl-9 pr-8 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          />
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" strokeWidth={2} />
        </div>
      )}

      {isOpen && !value && (
        <div
          ref={dropdownRef}
          className={`mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white ${
            inlineDropdown ? '' : `absolute z-50 ${OVERLAY_SHADOW}`
          }`}
        >
          {!hasResults && (
            <div className="px-3 py-4 text-[12px] text-slate-500 text-center">
              {noResultsText}
            </div>
          )}

          {filteredSuggestions.length > 0 && (
            <>
              <div className="kicker px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                {suggestedLabel}
              </div>
              {filteredSuggestions.map((option) => (
                <OptionItem
                  key={`suggested-${option.id}`}
                  option={option}
                  onSelect={handleSelect}
                />
              ))}
            </>
          )}

          {mainOptions.length > 0 && (
            <>
              {filteredSuggestions.length > 0 &&
                (optionsLabel ? (
                  <div className="kicker px-3 py-1.5 bg-slate-50 border-y border-slate-100">
                    {optionsLabel}
                  </div>
                ) : (
                  <div className="border-t border-slate-100" />
                ))}
              {mainOptions.map((option) => (
                <OptionItem key={option.id} option={option} onSelect={handleSelect} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OptionItem({
  option,
  onSelect,
}: {
  option: SearchSelectOption;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      className="w-full text-left px-3 py-2 text-[13px] hover:bg-slate-50 transition-colors"
    >
      <div className="text-slate-900 truncate">{option.label}</div>
      {option.description && (
        <div className="text-[11px] text-slate-500 truncate">{option.description}</div>
      )}
    </button>
  );
}
